
import { AddCircle, Delete, Edit, Sms } from '@mui/icons-material';
import { Badge, Box, Button, IconButton, Popover, Typography, styled } from '@mui/material';

import { AppModels, AppState, RelatedContactAttrs, contactSelector, fetchContact, formatPhoneNumber, formatPhoneNumberToUrl, hasErrors, notEmptyString, projectSelector, relatedContactSlice, saveModel } from 'cdx-common';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useToaster } from '../../libraries/Toaster';
import { appStore } from '../../store';
import { AppItemAvatar } from '../AppItems';
import { Pill } from '../utils/Pill';
import { EditProjectContact } from './EditProjectContact';

const Item = styled(Button)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'left',
    color: theme.palette.text.secondary,
    maxWidth: 250,
    flexGrow: 1,
}));

export const ProjectContactItem = (props: { onDelete?: () => void, relatedContact: RelatedContactAttrs, notInProjectId?: ModelId; }) => {

    const [hover, setHover] = React.useState(false);
    const [edit, setEdit] = React.useState(false);
    const [showDetails, setShoeDetails] = React.useState<Element | null>(null);
    const contact = useSelector((state: AppState) => contactSelector.selectById(state, props.relatedContact.contactId));
    const project = useSelector((state: AppState) => projectSelector.selectById(state, state.projectPage.project.id));
    const { toast } = useToaster();
    const addContact = async () => {
        if (props.notInProjectId) {
            const response = await saveModel(AppModels.relatedContacts, {
                ...props.relatedContact,
                id: '0',
                label: props.relatedContact.label,
                relatedType: AppModels.projects,
                relatedId: props.notInProjectId
            });
            if (!hasErrors(response)) {
                appStore.dispatch(relatedContactSlice.actions.upsertOne(response));
                toast.success("Contact ajouté");
            } else {
                toast.error("Ce contact ne peut être ajouté :(");
            }
        }
    };
    React.useEffect(() => { props.relatedContact.contactId != contact?.id && appStore.dispatch(fetchContact(props.relatedContact.contactId)); }, [contact, props.relatedContact.contactId]);

    if (!contact || !project) {
        return;
    }

    const wrapWithBadge = (content: React.ReactNode): React.ReactNode => {
        return props.onDelete ? <Badge badgeContent={<Delete onClick={props.onDelete} style={{ fontSize: 'inherit' }} />} color='error'>{content}</Badge> : content;
    };

    return <>
        {edit && <EditProjectContact relatedContact={props.relatedContact} contact={contact} onClose={() => setEdit(false)} />}
        {props.notInProjectId === undefined && <Popover
            id={'rc' + props.relatedContact.id}
            open={!!showDetails}
            anchorEl={showDetails}
            onClose={() => setShoeDetails(null)}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
            }}
        >
            <Box sx={{ minWidth: 200 }}>
                <Box sx={{ m: 2, display: 'flex', flexDirection: 'row' }}>
                    <AppItemAvatar model={contact} type={AppModels.contacts} sx={{ width: 75 }} />
                    <Box sx={{ flexGrow: 1, marginLeft: 1 }}>
                        <Typography fontSize={13} noWrap>{contact.name}</Typography>
                        <Typography fontSize={10} noWrap>{notEmptyString(props.relatedContact.label) ?? contact.title}</Typography>
                    </Box>
                </Box>
                <Box sx={{ m: 2 }}>
                    {contact.id === project.mainContactId ? <Typography fontSize={12}><Pill bgColor='#000'>cp</Pill>Contact principal du projet</Typography> : null}
                    {props.relatedContact.onSiteContact && <Typography fontSize={12}><Pill>cc</Pill>Contact de chantier</Typography>}
                    <Typography fontSize={12}><strong>Courriel : </strong> {notEmptyString(contact.email) ? <a href={'mailto:' + notEmptyString(contact.email)}>{notEmptyString(contact.email)}</a> : 'non spécifié'}</Typography>
                    <Typography fontSize={12}><strong>Cell.: </strong> {notEmptyString(contact.cell) ? <><a href={'tel:' + formatPhoneNumberToUrl(contact.cell)}>{formatPhoneNumber(contact.cell)}</a> <a href={'sms:' + formatPhoneNumber(contact.cell)}><Sms style={{ width: 12, height: 12 }} /></a></> : 'non spécifié'}</Typography>
                    <Typography fontSize={12}><strong>Tél.: </strong> {notEmptyString(contact.tel) ? <a href={'tel:' + formatPhoneNumberToUrl(contact.tel, contact.telPoste)}>{formatPhoneNumber(contact.tel, contact.telPoste)}</a> : 'non spécifié'}</Typography>
                </Box>
                <Box sx={{ m: 2 }}>
                    <Button size='small' onClick={() => {
                        setEdit(true);
                    }} variant='contained'>Modifier</Button>
                    <Button size='small' component={Link} to={'/contact/' + contact.id} variant='outlined'>Fiche</Button>
                </Box>
            </Box>
        </Popover>}
        <Item onMouseOver={() => setHover(true)} onMouseOut={() => setHover(false)} variant='outlined' onClick={(event) => setShoeDetails(event.currentTarget as Element)} sx={{ position: 'relative' }}>
            {props.notInProjectId && <Box sx={{ display: hover ? 'flex' : 'none', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.75)', color: 'white' }}>
                <IconButton onClick={() => addContact()}><AddCircle htmlColor='white' /></IconButton>
                <IconButton component={Link} to={'/contact/' + props.relatedContact.contactId}><Edit htmlColor='white' /></IconButton>
            </Box>}
            {wrapWithBadge(
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                        <Typography noWrap sx={{ flexGrow: 1 }} variant='body1'>{contact.name}</Typography>
                    </Box>
                    <Typography noWrap sx={{ flexGrow: 1 }} variant='caption'>
                        {contact.id == project.mainContactId && <Pill bgColor='#000'>CP</Pill>}
                        {props.relatedContact.onSiteContact && <Pill>CC</Pill>}
                        {notEmptyString(props.relatedContact.label) ?? (contact.title ? <em>{contact.title}</em> : null)}
                    </Typography>
                </Box>
            )}
        </Item>
    </>;
};
